// @flow

import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";

import type { State } from "@store/reducers";
import { getSelectedAbsenceReviewTask } from "@modules/shared/selectors/absence-review";
import { getAbsenceMonitoringAppealConfig } from "@modules/service/selectors/absence-monitoring";
import actionCreator from "@modules/shared/actionCreator";
import serviceActionCreator from "@modules/service/actionCreator";
import type { AbsenceReviewTask } from "@modules/shared/reducer/absence-review/types";
import { View } from "@components";
import { AppealForm, HearingForm } from "@modules/shared/components";

type PropsFromState = {
  reviewTask: ?AbsenceReviewTask,
  appealConfig: {
    appealReasons: Array<Object>,
    outcomeReasons: Array<Object>,
  },
};

type PropsFromDispatch = {
  getAbsenceReviewTask: typeof actionCreator.GetAbsenceReviewTask,
  resetSelectedAbsenceReviewTask: typeof actionCreator.ResetSelectedAbsenceReviewTask,
  getAbsenceMonitoring: typeof serviceActionCreator.GetAbsenceMonitoring,
  addAppeal: typeof actionCreator.AddEmployeeAppeal,
  updateAppeal: typeof actionCreator.UpdateEmployeeAppeal,
  resolveAppeal: typeof actionCreator.ResolveEmployeeAppeal,
};

type Props = PropsFromState &
  PropsFromDispatch & {
    clientId: number,
    employeeId: number,
    appealId: number,
    taskId: number,
    onClose: () => void,
  };

const AppealReview = (props: Props) => {
  const { appealId, reviewTask, appealConfig, onClose } = props;
  const absenceMonitoringProcessId = reviewTask?.absenceMonitoringProcessId;
  const submittedAppeal = reviewTask?.appeal;

  useEffect(() => {
    if (absenceMonitoringProcessId) {
      props.getAbsenceMonitoring({
        clientId: props.clientId,
        processId: absenceMonitoringProcessId,
      });
    }
  }, [absenceMonitoringProcessId]);

  useEffect(() => {
    props.getAbsenceReviewTask({
      clientId: props.clientId,
      taskId: props.taskId,
    });

    return () => {
      props.resetSelectedAbsenceReviewTask();
    };
  }, []);

  return (
    <View>
      {Boolean(appealConfig.appealReasons.length && appealConfig.outcomeReasons.length) && (
        <>
          <AppealForm
            submittedAppeal={submittedAppeal}
            appealReasons={appealConfig.appealReasons}
            clientId={props.clientId}
            employeeId={props.employeeId}
            taskId={props.taskId}
            addAppeal={props.addAppeal}
            updateAppeal={props.updateAppeal}
          />
          <HearingForm
            submittedAppeal={submittedAppeal}
            outcomeReasons={appealConfig.outcomeReasons}
            clientId={props.clientId}
            employeeId={props.employeeId}
            taskId={props.taskId}
            resolveAppeal={props.resolveAppeal}
            updateAppeal={props.updateAppeal}
            onClose={onClose}
          />
        </>
      )}
    </View>
  );
};

const mapStateToProps = (state: State): PropsFromState => ({
  reviewTask: getSelectedAbsenceReviewTask(state),
  appealConfig: getAbsenceMonitoringAppealConfig(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceReviewTask: actionCreator.GetAbsenceReviewTask,
  resetSelectedAbsenceReviewTask: actionCreator.ResetSelectedAbsenceReviewTask,
  getAbsenceMonitoring: serviceActionCreator.GetAbsenceMonitoring,
  addAppeal: actionCreator.AddEmployeeAppeal,
  updateAppeal: actionCreator.UpdateEmployeeAppeal,
  resolveAppeal: actionCreator.ResolveEmployeeAppeal,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(AppealReview);
